/**
 * External Imports
 */
import { FC, useCallback, useMemo, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { format, differenceInDays } from 'date-fns';

/**
 * Nexus Imports
 */
import { NeuIcon } from '@neutron/react';

/**
 * Internal Imports
 */
import { useLocation } from 'react-router-dom';
import Table from '../../../shared/table';

import { RootState } from '../../../../redux/store';
import {
  openModal,
  setModalType,
  setModalAction,
  getStoplightById,
  clearStoplightFilter,
  setCurrentStoplightPage,
  setStoplightSort,
  setUserSection
} from '../../../../redux/actions';

import { useStoplightsApi } from '../../../../utils/api';
import { useDebounceValue } from '../../../../utils/debouncers';
import { date } from '../../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import {
  AuthUserProps,
  Filter,
  Stoplight
} from '../../../../config/interfaces';

/**
 * Style Imports
 */
import {
  CellContainer,
  StyledBoldLabel,
  StyledLabel,
  StyledNeuTableRow
} from '../../../shared/table/Table.styles';

interface IStoplightTableProps {
  authorizedUser: AuthUserProps;
  currentStoplightPage: number;
  loading: boolean;
  location: string;
  locationStoplights: Stoplight[];
  searchTerm: string;
  selectedUnits: { id: string; unit: string }[];
  stoplightFilter: Filter;
  totalResults: number;
}

const StoplightTable: FC<IStoplightTableProps> = ({
  authorizedUser,
  currentStoplightPage,
  loading,
  location,
  locationStoplights,
  searchTerm,
  selectedUnits,
  stoplightFilter,
  totalResults
}) => {
  const dispatch = useDispatch();

  const [sortKey, setSortKey] = useState<string>('created');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const [active, setActive] = useState<string>('Created');
  const { from = '' } = useLocation().state || {};

  const dbSearchTerm = useDebounceValue(searchTerm, 300);

  const handleSort = (column: string) => {
    let key;
    switch (column) {
      case 'Created':
        key = 'created';
        break;
      case 'Category/Subcategory':
        key = 'category';
        break;
      case 'Issues':
        key = 'attachedIssues';
        break;
      case 'Last Comment':
        key = 'lastUpdate';
        break;
      case 'Status':
        key = 'status';
        break;
      default:
        key = '';
    }
    if (key === sortKey && sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortDir('asc');
    }
    setActive(column);
    setSortKey(key);
  };

  const openStoplight = useCallback((stoplight: Stoplight) => {
    if (stoplight.id) {
      dispatch(getStoplightById(stoplight.id));
      if (
        stoplight.createdBy ===
        `${authorizedUser.firstName} ${authorizedUser.lastName}`
      ) {
        dispatch(setModalType('Edit Stoplight'));
        dispatch(setModalAction('EDIT'));
      } else {
        dispatch(setModalType('Edit Stoplight'));
        dispatch(setModalAction('VIEW'));
      }

      dispatch(openModal());
    }
  }, []);

  const isTableEmpty = useMemo(
    () => !dbSearchTerm && locationStoplights.length === 0,
    [dbSearchTerm, locationStoplights]
  );

  const isTableEmptySearch = useMemo(
    () => dbSearchTerm && locationStoplights.length === 0,
    [dbSearchTerm, locationStoplights]
  );

  useEffect(() => {
    // clear stoplight filters & reset page when navigating away
    return () => {
      dispatch(clearStoplightFilter());
      dispatch(setCurrentStoplightPage(1));
      dispatch(setStoplightSort({ sortKey: 'created', sortDir: 'desc' }));
    };
  }, []);

  useEffect(() => {
    if (from === 'patient/summary') {
      dispatch(setUserSection('patient'));
    } else if (from === 'employee/summary') {
      dispatch(setUserSection('employee'));
    }
    const stoplightAction = useStoplightsApi({
      authorizedUser,
      currentStoplightPage,
      searchTerm: dbSearchTerm,
      selectedUnits,
      sortKey,
      sortDir,
      stoplightFilter
    });
    dispatch(stoplightAction);
  }, [
    authorizedUser,
    currentStoplightPage,
    dbSearchTerm,
    location,
    selectedUnits,
    sortKey,
    sortDir,
    stoplightFilter
  ]);

  useEffect(() => {
    dispatch(setStoplightSort({ sortKey, sortDir }));
  }, [sortDir, sortKey]);

  const headers: JSX.Element[] = [
    <CellContainer
      key="Created"
      id="Stoplight-Table-Created-Column"
      unit="column"
      colWidth={13}
      direction={!(active === 'Created') ? 'none' : sortDir}
      sort
    >
      Created
    </CellContainer>,
    <CellContainer
      key="Location"
      id="Stoplight-Table-Location-Column"
      unit="column"
      colWidth={10}
      direction="none"
    >
      {location === 'patient' ? 'Location' : 'Department'}
    </CellContainer>,
    <CellContainer
      key="Category/Subcategory"
      id="Stoplight-Table-Category-Column"
      unit="column"
      colWidth={17}
      direction={!(active === 'Category/Subcategory') ? 'none' : sortDir}
      sort
    >
      Category/Subcategory
    </CellContainer>,
    <CellContainer
      key="Description"
      id="Stoplight-Table-Description-Column"
      unit="column"
      colWidth={20}
      direction="none"
    >
      Description
    </CellContainer>,
    <CellContainer
      key="Issues"
      id="Stoplight-Table-Issues-Column"
      unit="column"
      colWidth={8}
      direction={!(active === 'Issues') ? 'none' : sortDir}
      sort
    >
      Issues
    </CellContainer>,
    <CellContainer
      key="Last Comment"
      id="Stoplight-Table-Update-Column"
      unit="column"
      colWidth={20}
      direction={!(active === 'Last Comment') ? 'none' : sortDir}
      sort
    >
      Last Comment
    </CellContainer>,
    <CellContainer
      key="Status"
      id="Stoplight-Table-Status-Column"
      unit="column"
      colWidth={12}
      direction={!(active === 'Status') ? 'none' : sortDir}
      sort
      styles="align-items: center"
    >
      Status
    </CellContainer>
  ];

  const rows = locationStoplights?.map((stoplight: Stoplight, i: number) => (
    <StyledNeuTableRow
      // eslint-disable-next-line react/no-array-index-key
      key={`stoplight-row-${i}`}
      data-testid={`stoplight-row-${stoplight.friendlyId}`}
      unit="row"
      size="default"
      height="8em"
      role="button"
      styles={
        stoplight.status === 'Completed'
          ? 'background-color: #f6f6f6 !important'
          : ''
      }
      onClick={() => openStoplight(stoplight)}
    >
      <CellContainer unit="column" colWidth={13} alignment="start">
        <StyledBoldLabel>{stoplight.friendlyId}</StyledBoldLabel>
        <StyledBoldLabel
          className="neu-text-gray-90"
          style={{ fontWeight: 600 }}
        >
          {stoplight.createdBy}
        </StyledBoldLabel>
        <StyledLabel>{date.dayOfYrFull(stoplight.created)}</StyledLabel>
        <StyledLabel>
          {stoplight.status !== 'Completed'
            ? `${differenceInDays(
                new Date(),
                date.parseStopDate(stoplight.created)
              )}d`
            : ''}
        </StyledLabel>
      </CellContainer>
      <CellContainer unit="column" colWidth={10} alignment="start">
        <StyledBoldLabel className="neu-text-gray-90">
          {location === 'patient' && stoplight.locations?.unit.length > 0
            ? stoplight.locations.unit[0].displayName
            : location === 'employee' &&
              stoplight.locations?.department.length > 0
            ? stoplight.locations?.department[0].displayName
            : 'Unknown'}
        </StyledBoldLabel>
      </CellContainer>
      <CellContainer unit="column" colWidth={17} alignment="start">
        <StyledBoldLabel className="neu-text-gray-90">
          {stoplight.category}
        </StyledBoldLabel>
        <StyledLabel className="neu-text-gray-90">
          {stoplight.subCategory}
        </StyledLabel>
      </CellContainer>
      <CellContainer unit="column" colWidth={20} alignment="start">
        <StyledLabel className="neu-text-gray-90 text-clamp text-clamp__3">
          {stoplight.details}
        </StyledLabel>
      </CellContainer>
      <CellContainer unit="column" colWidth={8} alignment="start">
        <StyledBoldLabel
          className="neu-text-gray-90"
          style={{ paddingLeft: '1rem' }}
        >
          {stoplight.issueIds?.length}
        </StyledBoldLabel>
      </CellContainer>
      <CellContainer unit="column" colWidth={20} alignment="start">
        <div>
          {stoplight.latestComment ? (
            <>
              <StyledLabel className="neu-text-gray-90">
                {stoplight.latestComment.date
                  ? format(
                      date.parseStopDate(stoplight.latestComment.date),
                      'MM/dd/yyyy'
                    )
                  : 'date'}{' '}
                |{' '}
                {stoplight.latestComment.authorFullName
                  ? stoplight.latestComment.authorFullName
                  : 'FirstName LastName'}{' '}
              </StyledLabel>
              <StyledLabel className="neu-text-gray-90 text-clamp text-clamp__3">
                {stoplight.latestComment.body
                  ? stoplight.latestComment.body
                  : ''}
              </StyledLabel>
            </>
          ) : (
            'No Comments'
          )}
        </div>
      </CellContainer>
      <CellContainer
        unit="column"
        colWidth={12}
        alignment="start"
        styles="align-items: center;"
      >
        <NeuIcon
          className={
            stoplight.status === 'Completed'
              ? 'neu-text-green-60'
              : stoplight.status === 'In Progress'
              ? 'neu-text-yellow-30'
              : 'neu-text-red-60'
          }
        >
          circle
        </NeuIcon>
        <StyledBoldLabel
          className={`${
            stoplight.status === 'Completed'
              ? 'neu-text-green-80'
              : stoplight.status === 'In Progress'
              ? 'neu-text-yellow-80'
              : 'neu-text-red-80'
          } px-1`}
        >
          {stoplight.status}
        </StyledBoldLabel>
      </CellContainer>
    </StyledNeuTableRow>
  ));

  return (
    <Table
      headers={headers}
      rows={rows}
      isTableEmpty={isTableEmpty}
      isTableEmptySearch={isTableEmptySearch}
      loading={loading}
      totalResults={totalResults}
      type="stoplight"
      onSort={(e: any) => handleSort(e.detail.label)}
    />
  );
};

const mapReduxStateToProps = (state: RootState) => ({
  authorizedUser: state.AuthorizedUser?.authorizedUser,
  selectedUnits: state.ConfigReducer.selectedUnits,
  stoplightFilter: state.StoplightReducer.stoplightFilter,
  location: state.UserReducer.userSection,
  locationStoplights: state.StoplightReducer.locationStoplights,
  totalResults: state.StoplightReducer.totalCount,
  currentStoplightPage: state.StoplightReducer.currentStoplightPage,
  loading: state.StoplightReducer.loading
});

export default connect(mapReduxStateToProps)(StoplightTable);
